import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@mui/styles';

import TextField from '@mui/material/TextField';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';

import { get as _get } from 'lodash';

import { GroupsContext } from '../../../../data/groups-service';
import Validator, { useValidate, checkValidity } from '../../../../components/validator';
import DashboardService from '../../../../data/dashboard-service';
import DataContext from '../../../../data/data-context.js';
import MC2HealthDataService from '../../../../data/mc2health-service.js';
import MultiGauge from '../../../../components/charts/multigauge';
import Auth from '../../../../data/auth-service';

const useStyles = makeStyles(theme => ({
    root: {
    },
    label: {
        textTransform: 'capitalize',
        display: 'inline',
    },
    secondary: {
        backgroundColor: theme.palette.secondary.main,
    },
    primary: {
        backgroundColor: theme.palette.primary.main,
    },
    // editor classes
    editorRoot: {
        padding: theme.spacing(1),
    },
    editorFooter: {
        alignSelf: 'flex-end',
        width: '100%',
    },
    editorMargin: {
        margin: theme.spacing(2),
    },
    inlineChips: {
        display: 'inline-block',
        verticalAlign: 'middle',
        padding: 5,
        height: 30,
        paddingTop: 0,
    },
    inlineForm: {
        margin: theme.spacing(1),
        '& .MuiTextField-root': {
            margin: theme.spacing(1),
        },
    },
}));

const DEFAULTS = edit => edit
    ? { minW: 12, minH: 10 }
    : { minW: 1, minH: 5 };

// don't display until size-me can figure out what our size is
const MC2HealthMetricWidget = (props) => {
    return !props.size || !props.size.width ? ''
        : (
            props.data.edit
                ? <Editor { ...props }/>
                : <Widget { ...props } />
        );
};
MC2HealthMetricWidget.propTypes = {
    data: PropTypes.object.isRequired,
};

const Widget = (props) => {
    const classes = useStyles();
    const { cmcs } = useContext(GroupsContext);

    const [, setErrors] = useState(0);
    const [data, setData] = useState([]);

    const fetch = controller => {
        props.setBusy(true);
        const SystemHealth = Auth.permissions.get('SystemHealth');
        const CMC = Auth.permissions.get('CMC');

        if (props.data.settings.type === 'Sensor' && CMC.read) {
            return DashboardService.mc2GetSensorHealth({
                cmc: props.data.settings.targetcmc, // this is a name
                sensor: props.data.settings.targetsensor, // this is a UUID
            }, controller).catch(err => {
                props.onError(err, props.data.title);
                setErrors(current => {
                    props.setBusy(false, current > 2);
                    if (current > 2) DashboardService.unsubscribe(props.data.id);
                    return current + 1;
                });
                return 'error';
            });
        } else if (props.data.settings.type === 'CMC' && CMC.read) {
            const cmc = cmcs.find(c => c.name === props.data.settings.targetcmc);
            if (!cmc) {
                console.log("don't have cmc", props.data.settings.targetcmc, cmcs);
                setErrors(current => {
                    props.setBusy(false, current > 2);
                    if (current > 2) DashboardService.unsubscribe(props.data.id);
                    return current + 1;
                });
                return Promise.reject(new Error('invalid cmc'));
            }
            return DashboardService.mc2GetCMCHealth({
                cmc: cmc.uuid,
            }, controller).catch(err => {
                props.onError(err, props.data.title);
                setErrors(current => {
                    props.setBusy(false, current > 2);
                    if (current > 2) DashboardService.unsubscribe(props.data.id);
                    return current + 1;
                });
                return 'error';
            });
        } else if (SystemHealth.Read)/* MC2 */ {
            return MC2HealthDataService.getSystemHealth()
                .catch(err => {
                    props.onError(err, props.data.title);
                    setErrors(current => {
                        props.setBusy(false, current > 2);
                        if (current > 2) DashboardService.unsubscribe(props.data.id);
                        return current + 1;
                    });
                    return 'error';
                });
        } else {
            props.onError('unauthorized', props.data.title);
            return Promise.resolve('unauthorized');
        }
    };

    const callback = res => {
        if (res === 'error') return;
        setErrors(0);
        props.setBusy(false);

        let fn;
        let severities;
        switch (props.data.settings.gauge) {
            case 'CPU':
                fn = d => _get(d, 'health_metrics.incr_cpuinfo_pct.total.used') || 0;
                break;
            case 'RAM':
                fn = d => {
                    const avail = _get(d, 'health_metrics.system.meminfo.avail_memory') || 0;
                    const total = _get(d, 'health_metrics.system.meminfo.total_memory') || 1;
                    return 100 * (1.0 - avail / total);
                };
                break;
            case 'Disk':
                fn = d => {
                    const disk = (_get(d, 'health_metrics.disks') || [])
                        .find(d => d.mount_point === '/') || { utilization: 0 };
                    return 100 * disk.utilization;
                };
                severities = { green: 82, red: 101 };
                break;
            case 'Inodes':
                fn = d => {
                    const disk = (_get(d, 'health_metrics.disks') || [])
                        .find(d => d.mount_point === '/') || { inode_utilization: 0 };
                    return 100 * disk.inode_utilization;
                };
                break;
        }

        setData({
            val: fn(res),
            severities,
            title: props.data.settings.gauge
        });
    };

    const subscribe = () => {
        setErrors(0);
        DashboardService.subscribe(props.data.id, fetch, callback);
    };

    // incoming
    useEffect(() => {
        subscribe();
        return () => {
            // unsubscribe to data feed while editing (widget control exit)
            // will also force a refresh when resubscribing after edit is done
            DashboardService.unsubscribe(props.data.id);
        };
    }, []);

    return (
        <div
            className={classes.root}
            style={{ width: props.size.width, height: props.size.height }}>
            <MultiGauge
                label={data.title || ''}
                value={data.val || 0}
                severities={data.severities}
                size={Math.min(props.size.width, props.size.height)}
                small={props.data.settings.label === 'Bottom'}/>
        </div>
    );
};

Widget.propTypes = {
    data: PropTypes.object.isRequired,
    size: PropTypes.object,
    hover: PropTypes.bool,
    setBusy: PropTypes.func,
    onError: PropTypes.func,
};

const Editor = (props) => {
    const classes = useStyles();

    const { sensorNames } = useContext(DataContext);
    const { cmcs } = useContext(GroupsContext);

    const [title, setTitle] = useState(props.data.title);
    const [type, setType] = useState(props.data.settings.type || 'MC2');
    // this is a name
    const [targetcmc, setTargetcmc] = useState(props.data.settings.targetcmc);
    // this is a uuid
    const [targetsensor, setTargetsensor] = useState(props.data.settings.targetsensor);
    const [gauge, setGauge] = useState(props.data.settings.gauge || 'CPU');
    const [label, setLabel] = useState(props.data.settings.label || 'Center');

    const [sensors, setSensors] = useState([]);

    const handleTitleChange = (e, validID, valid) => {
        setTitle(e.target.value);
    };

    const handleTypeChange = e => setType(e.target.value);
    const handleTargetsensorChange = e => setTargetsensor(e.target.value);
    const handleTargetcmcChange = e => setTargetcmc(e.target.value);
    const handleGaugeChange = e => setGauge(e.target.value);
    const handleLabelChange = e => setLabel(e.target.value);

    const handleSaveChanges = () => {
        props.onOK({
            ...props.data,
            title,
            settings: {
                ...props.data.settings,
                type,
                targetcmc,
                targetsensor,
                gauge
            },
        });
    };

    const isValid = () => {
        if (!gauge) return false;
        if (!type) return false;
        if (type !== 'MC2' && !targetcmc) return false;
        if (type === 'Sensor' && !targetsensor) return false;
        return true;
    };

    useEffect(() => {
        // filter available sensor list
        setSensors(Object.entries(sensorNames)
            .filter(([k, v]) => v.cmc === targetcmc)
            .map(([k, v]) => ({ ...v, uuid: k })));

        setTargetsensor(current => {
            if (sensorNames[current] && sensorNames[current].cmc !== targetcmc) return '';
            return current;
        });
    }, [sensorNames, targetcmc]);

    return (
        <Box
            display="flex" flexWrap="wrap"
            className={classes.editorRoot}
            style={{ width: props.size.width - 5, height: props.size.height - 20 }}>
            <Box>
                <div className={classes.inlineForm}>
                    <Validator
                        style={{ width: 200 }}
                        label="Title"
                        aria-label="Title"
                        value={title}
                        onChange={handleTitleChange}/>
                    {/* gauge */}
                    <TextField
                        style={{ width: 100 }}
                        select
                        label="Metric"
                        aria-label="Metric"
                        value={gauge}
                        onChange={handleGaugeChange}>
                        {['CPU', 'RAM', 'Disk', 'Inodes'].map((option) => (
                            <MenuItem key={option} value={option}>
                                {option}
                            </MenuItem>
                        ))}
                    </TextField>
                    {/* label position */}
                    <TextField
                        style={{ width: 100 }}
                        select
                        label="Label"
                        aria-label="Label"
                        value={label}
                        onChange={handleLabelChange}>
                        {['Center', 'Bottom'].map((option) => (
                            <MenuItem key={option} value={option}>
                                {option}
                            </MenuItem>
                        ))}
                    </TextField>
                </div>
                <div className={classes.editorMargin}>
                    <Grid container spacing={1} alignItems="flex-end">
                        {/* type */}
                        <Grid item>
                            <TextField
                                style={{ width: 100 }}
                                select
                                label="Alert Source"
                                aria-label="Alert Source"
                                value={type}
                                onChange={handleTypeChange}>
                                {['MC2', 'CMC', 'Sensor'].map((option) => (
                                    <MenuItem key={option} value={option}>
                                        {option}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        {/* cmc */}
                        {type !== 'MC2' && <Grid item>
                            <TextField
                                style={{ width: 100 }}
                                select
                                label="CMC"
                                aria-label="CMC"
                                value={targetcmc}
                                onChange={handleTargetcmcChange}>
                                {(cmcs || []).map((option) => (
                                    <MenuItem key={option.uuid} value={option.name}>
                                        {option.name}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>}
                        {/* sensor */}
                        {type === 'Sensor' && sensors.length && <Grid item>
                            <TextField
                                style={{ width: 100 }}
                                select
                                label="Sensor"
                                aria-label="Sensor"
                                value={targetsensor}
                                onChange={handleTargetsensorChange}>
                                {sensors.map((option) => (
                                    <MenuItem key={option.uuid} value={option.uuid}>
                                        {option.name}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>}
                    </Grid>
                </div>
            </Box>
            <Box className={classes.editorFooter}>
                <DialogActions style={{ paddingBottom: 0 }}>
                    <Button
                        aria-label="ok"
                        onClick={handleSaveChanges}
                        color="primary"
                        disabled={!isValid()}>
                    OK
                    </Button>
                    <Button aria-label="cancel" onClick={props.onCancel} color="primary" autoFocus>
                    Cancel
                    </Button>
                </DialogActions>
            </Box>
        </Box>
    );
};
Editor.propTypes = {
    data: PropTypes.object.isRequired,
    size: PropTypes.object,
    onOK: PropTypes.func,
    onCancel: PropTypes.func,
};

export {
    MC2HealthMetricWidget as default,
    DEFAULTS,
};
